<template>
    <div class="m-2">

        <b-row>
            <b-col md="6" lg="6">
                <ProductSection
                    :section="reply.sections.product"
                />
            </b-col>
            <b-col md="6" lg="6">
                <Conversation key="sent-request-reply" :request-id="requestId" />
            </b-col>
        </b-row>
        <b-row v-if="reply.sections.fmd">
            <div class="mb-1 mt-1 pl-1"><h3>{{ $t('Page(SentRequest):Component(Text):fmd') }}</h3></div>
        </b-row>
        <b-row v-if="reply.sections.fmd">
            <b-col>
                <FMDSection
                    :product-id="request.product.id"
                    :section="reply.sections.fmd"
                />
            </b-col>
        </b-row>

        <b-row v-if="showConcernSubstancesSection">
            <div class="mb-1 mt-1 pl-1"><h3>{{ $t('Page(SentRequest):Component(Text):replied-concern-elements') }}</h3></div>
        </b-row>
        <b-row v-if="showConcernSubstancesSection">
            <b-col>
                <ConcernSubstancesSection
                    :section="reply.sections.concern_substances"
                    :legislations="request.legislations"
                />
            </b-col>
        </b-row>

        <b-row v-if="showFmdSubstanceLists">
            <div class="mb-1 mt-1 pl-1"><h3>{{ $t('Page(SentRequest):Component(Text):replied-concern-elements') }}</h3></div>
        </b-row>
        <b-row v-if="showFmdSubstanceLists">
            <b-col>
                <SubstanceListsWithQuestions
                    :product="fmdSubstanceListsProduct"
                    :request="request"
                    :included-substances="fmdIncludedSubstances"
                    :included-list-ids="fmdIncludedListIds"
                    :loading="fmdIncludedListsLoading"
                    :read-only="true"
                    :collapse-questionless-referenced-lists="true"
                    radio-name-prefix="sent-request-reply-question-for"
                />
            </b-col>
        </b-row>

        <b-row v-if="reply.sections.components_concern_elements">
            <div class="mb-1 mt-1 pl-1"><h3>{{ $t('Page(SentRequest):Component(Text):composition') }}</h3></div>
        </b-row>
        <b-row v-if="reply.sections.components_concern_elements">
            <b-col>
                <ComponentConcernElements
                    class="mb-1"
                    v-for="container in reply.sections.components_concern_elements"
                    :key="container.component ? container.component.id : container.id"
                    :container="container"
                    :substance-lists="substanceLists"
                />
                <b-alert
                    variant="warning"
                    :show="showConcernSubstancesAreNotDefined"
                >
                    <div class="alert-body">
                        <span>{{ $t('Component(Text):concern-substances-not-defined') }}</span>
                    </div>
                </b-alert>
            </b-col>
        </b-row>

        <RegulationQuestionsSection
            v-if="showLegislationQuestionAnswersSection"
            :section="legislationQuestionAnswersSection"
            :groups="legislationQuestionnaire"
            :legislations="request.legislations"
            :loading="legislationQuestionnaireLoading"
        />

        <DocumentsSection
            v-if="reply.sections.documents"
            :request-id="request.id"
            :product-section="reply.sections.product"
            :section="reply.sections.documents"
            :verification-documents="request.verificationDocuments"
        />

        <InformationToCustomerSection
            v-if="reply.sections.information_to_customer"
            :section="reply.sections.information_to_customer"
        />

        <DeclarationValiditySection
            v-if="reply.sections.validity_of_information"
            :section="reply.sections.validity_of_information"
        />
    </div>
</template>

<script>
import {
    BCard, BRow, BCol, BFormCheckbox, BFormTextarea,
    BFormDatepicker, BCardGroup, BFormGroup, BFormInput, BAlert,
    BBadge, BCardTitle, BCardHeader, BListGroup, BListGroupItem,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import ConcernElement from "@/views/request/sent/components/ConcernElement";
import DeclarationApproval from "@/views/request/received/components/DeclarationApproval";
import Instruction from "@/views/product/components/product-card/verification/Instruction.vue";
import {Conversation} from "@core/components/messenger";
import ReplyTabProductSection from "@core/mixins/ui/reply-tab-product-section";
import {VerificationDocumentType} from "@/libs/product";
import ComponentConcernElements from "@/views/request/sent/components/ComponentConcernElements";
import {
    CompliantStatementTable, EvidenceReportTable, DeclarationOfConformityTable
} from "@/views/product/components/product-card/verification";
import ReplyDocumentPreview from "@/views/request/sent/components/ReplyDocumentPreview.vue";
import downloadProductDocument from "@core/mixins/download-product-document";
import {
    ProductSection, FMDSection, DocumentsSection, InformationToCustomerSection, DeclarationValiditySection,
    ConcernSubstancesSection, RegulationQuestionsSection,
} from "@/views/request/sent/components/sections";
import {
    FmdDerivedSubstanceListsMixin,
    SubstanceListsWithQuestions,
} from "@/views/product/components/product-card/fmd";
import LegislationQuestionnaireMixin from '@/views/product/components/product-card/LegislationQuestionnaireMixin'

export default {
    mixins: [ReplyTabProductSection, downloadProductDocument, FmdDerivedSubstanceListsMixin, LegislationQuestionnaireMixin],
    components: {
        SubstanceListsWithQuestions,
        RegulationQuestionsSection,
        ConcernSubstancesSection,
        ProductSection, FMDSection, DocumentsSection, InformationToCustomerSection, DeclarationValiditySection,
        ReplyDocumentPreview,
        CompliantStatementTable,
        BCard, BRow, BCol, Conversation,
        BFormCheckbox, BListGroup, BListGroupItem,
        BFormTextarea, BFormDatepicker, BCardGroup, BCardTitle,
        BFormGroup, BFormInput, BAlert, BBadge, BCardHeader,
        ConcernElement, ComponentConcernElements,
        DeclarationOfConformityTable,
        EvidenceReportTable,
        DeclarationApproval,
        Instruction,
        vSelect,
    },
    props: {
        reply: {
            type: Object,
            required: true
        },
        request: {
            type: Object,
            required: true,
        },
        substanceLists: {
            type: Array,
            default() {
                return [];
            }
        },
    },
    data() {
        return {
            requestId: this.request.id,
            previewFile: null,
        };
    },
    methods: {
        getLegislationQuestionnaireLegislations() {
            return Array.isArray(this.request.legislations) ? this.request.legislations : [];
        },
        getLegislationQuestionAnswersProductId() {
            return null;
        },
        fetchLegislationQuestionAnswers() {
            return Promise.resolve([]);
        },
    },

    computed: {
        isFmdReply() {
            return Boolean(this.reply && this.reply.sections && this.reply.sections.fmd);
        },
        showConcernSubstancesSection() {
            return Boolean(
                this.reply
                && this.reply.sections
                && this.reply.sections.concern_substances
                && !this.isFmdReply
            );
        },
        showFmdSubstanceLists() {
            return this.isFmdReply;
        },
        legislationQuestionAnswersSection() {
            return this.reply
                && this.reply.sections
                && this.reply.sections.legislation_question_answers
                ? this.reply.sections.legislation_question_answers
                : null;
        },
        showLegislationQuestionAnswersSection() {
            return Boolean(
                this.legislationQuestionAnswersSection
                && Array.isArray(this.legislationQuestionAnswersSection.answers)
                && this.legislationQuestionAnswersSection.answers.length > 0
            );
        },
        fmdSubstanceListQuestionStates() {
            const section = this.reply
                && this.reply.sections
                && this.reply.sections.substance_list_question_states
                ? this.reply.sections.substance_list_question_states
                : null;

            return section && Array.isArray(section.states) ? section.states : [];
        },
        fmdSubstanceListsProduct() {
            return {
                ...this.request.product,
                substanceListQuestionStates: this.fmdSubstanceListQuestionStates,
            };
        },
        safeUseInstruction() {
            if (false === this.reply.product.isSafeUseApplicable) {
                return null;
            }

            return this.reply.safeUseInstruction;
        },
        showConcernSubstancesAreNotDefined() {
            if (!this.reply.product) {
                return false;
            }
            if (null === this.reply.product.hasConcernElements && 0 === this.reply.componentConcernElements.length) {
                return true;
            }

            if (this.reply.product.hasConcernElements === true) {
                return this.reply.componentConcernElements.length === 0;
            }

            return false;
        },


    },
    watch: {
        reply: {
            handler(reply) {
                if (!reply || !reply.sections || !reply.sections.fmd) {
                    this.fmdIncludedSubstances = [];
                    this.fmdIncludedListIds = [];
                    return;
                }

                this.syncFmdDerivedSubstanceLists(reply.sections.fmd.declarations || []);
            },
            immediate: true
        },
        request(r) {
            this.requestId = r.id;
        }
    }
}
</script>

<style type="text/css">
table tbody th {
    text-align: right;
    padding: 4px;
}
</style>
